import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LoginRequest, MeResponse } from '@seo/types';
import { apiGet, apiPost } from '@/lib/api';
import { type AuthTenant, type AuthUser, useAuthStore } from '@/lib/auth';
import type { MemberRole } from '@seo/types';

interface LoginResponse {
  access_token: string;
  expires_in: number;
  token_type: 'Bearer';
}

export function useLogin() {
  const signIn = useAuthStore((s) => s.signIn);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LoginRequest) => {
      // Purge any cached data from a prior session before authenticating —
      // otherwise logging into account B while A's queries are still cached
      // shows A's projects/audits/etc for a beat. Logout already clears the
      // cache; login must too for the "switched accounts without logging out"
      // path (common when session tokens were still valid).
      qc.clear();
      const tokens = await apiPost<LoginResponse>('/v1/auth/login', input, {
        unauthenticated: true,
      });
      // After login we don't know the active tenant/role; hit /v1/me with the
      // fresh access token so we can populate the store atomically.
      const me = await apiGet<MeResponse>('/v1/me', {
        headers: { Authorization: `Bearer ${tokens.access_token}` },
        // No tenant header yet — /v1/me derives the default tenant from the JWT.
        tenantId: null,
      });
      return { tokens, me };
    },
    onSuccess: ({ tokens, me }) => {
      const tenant: AuthTenant = {
        id: me.tenant.id,
        name: me.tenant.name,
        slug: me.tenant.slug,
        plan: me.tenant.plan,
        feature_flags: me.tenant.feature_flags,
      };
      const user: AuthUser = {
        id: me.user.id,
        email: me.user.email,
        display_name: me.user.display_name,
      };
      signIn({
        accessToken: tokens.access_token,
        tenant,
        user,
        role: me.role as MemberRole,
      });
    },
  });
}
